import type { Row } from "./api";
import { patch } from "./api";
import { canCapture } from "./capture";
import { validateIntakeValue, hasTechnicalDependency } from "./intake-values";
import type { IntakeCard } from "./components/IntakeExperience";
import { topicSaveValue, topics, type TaxTopicBinding } from "./tax-topics";
import { validateTaxFields } from "./field-validation";
import { filingStatusFieldIssues } from "./filing-status-presentation";
import {
  deriveFilingInterview,
  parseFilingInterview,
  filingInterviewValid,
  stripFilingInterview,
} from "./filing-status";
import {
  normalizeTaxAge,
  deriveTaxYearAge,
  taxAnswerView,
  isTaxObject,
} from "./tax-field-values";

export const topicKey = (card: IntakeCard) =>
  `${card.captureKind}:${card.record.id}`;
export const topicContainer = (card: IntakeCard): "answers" | "facts" =>
  card.captureKind === "questionnaire" ? "answers" : "facts";
export const storedTopicValue = (selected: Row, card: IntakeCard) =>
  selected[topicContainer(card)]?.[card.record.id];
export const cloneTopicValue = (value: unknown): unknown =>
  value === undefined ? undefined : JSON.parse(JSON.stringify(value));
export function topicValueKey(value: unknown): string {
  const ordered = (item: unknown): unknown =>
    Array.isArray(item)
      ? item.map(ordered)
      : item && typeof item === "object"
        ? Object.fromEntries(
            Object.entries(item)
              .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
              .map(([key, child]) => [key, ordered(child)]),
          )
        : item;
  return JSON.stringify(ordered(value));
}
/** Apply the edit delta against the newest local object, not an older rendered sibling value. */
export function mergeTopicEdit(
  current: unknown,
  rendered: unknown,
  next: unknown,
): unknown {
  if (topicValueKey(rendered) === topicValueKey(next))
    return cloneTopicValue(current);
  const object = (value: unknown): value is Row =>
    Boolean(value && typeof value === "object" && !Array.isArray(value));
  if (!object(next) || (rendered !== undefined && !object(rendered)))
    return cloneTopicValue(next);
  const before = object(rendered) ? rendered : {},
    result: Row = object(current) ? { ...current } : {};
  for (const key of Object.keys(before))
    if (!Object.prototype.hasOwnProperty.call(next, key)) delete result[key];
  for (const [key, value] of Object.entries(next))
    if (
      !Object.prototype.hasOwnProperty.call(before, key) ||
      topicValueKey(before[key]) !== topicValueKey(value)
    ) {
      Object.defineProperty(result, key, {
        value: mergeTopicEdit(result[key], before[key], value),
        enumerable: true,
        writable: true,
        configurable: true,
      });
    }
  return result;
}
export interface TopicChange {
  card: IntakeCard;
  value: unknown;
}
export interface TopicRequest {
  returnId: string;
  revision: number;
  topicId: string;
  url: string;
  body: Row;
}
// Question answers are local drafts, not certified answer contracts. Validate supplied
// leaves without manufacturing missing responses. Facts retain their full source schema.
export function questionDraftSchema(schema: Row): Row {
  const visit = (node: unknown): unknown => {
    if (Array.isArray(node)) return node.map(visit);
    if (!node || typeof node !== "object") return node;
    const next: Row = {};
    for (const [key, value] of Object.entries(node)) {
      if (key === "required") continue;
      next[key] = visit(value);
    }
    // A supplied wrapper must remain structurally honest, even in an incomplete draft.
    if (next.properties?.state)
      next.required =
        next.properties.state.const === "KNOWN"
          ? ["state", "value"]
          : ["state"];
    return next;
  };
  return visit(schema) as Row;
}
export function topicValueValid(card: IntakeCard, value: unknown): boolean {
  if (
    card.record.id === "Q-008" &&
    isTaxObject(value) &&
    Object.hasOwn(value, "interview")
  ) {
    if (!filingInterviewValid(value.interview)) return false;
    // Preserve incomplete interview drafts but never save an actively supplied malformed tax ID.
    if (
      filingStatusFieldIssues(value, "save-field").some(
        (issue) => issue.path[0] === "spouseTaxId" && issue.kind === "invalid",
      )
    )
      return false;
    value = stripFilingInterview(value);
  }
  const known = taxAnswerView(value).value;
  const ageSchema =
    card.schema["x-intake-stateful"]?.valueSchema ?? card.schema;
  const hasAgePair = Boolean(
    ageSchema.properties?.date_of_birth && ageSchema.properties?.age_for_2025,
  );
  const validBirthDate =
    !hasAgePair ||
    !isTaxObject(known) ||
    known.date_of_birth === undefined ||
    deriveTaxYearAge(known.date_of_birth) !== undefined;
  const invalidFields = validateTaxFields(card.schema, value, {
    sectionId: "intake",
    sectionLabel: "Tax Interview",
    rootLabel: card.title,
    rootId: "save-field",
  }).filter((issue) => {
    if (issue.code === "ANSWER_UNKNOWN" || issue.code === "ANSWER_CONFLICT")
      return false;
    // An explicitly captured conflict is a permitted draft state, not a malformed
    // field value. It remains visible for review without changing admission logic.
    let at: unknown = value;
    for (const segment of issue.path) {
      at = taxAnswerView(at).value;
      at =
        isTaxObject(at) || Array.isArray(at) ? (at as Row)[segment] : undefined;
    }
    if (
      issue.kind === "required" &&
      card.captureKind === "questionnaire" &&
      at === undefined
    )
      return false;
    return at !== "CONFLICT" && !(isTaxObject(at) && at.state === "CONFLICT");
  });
  return (
    value !== undefined &&
    card.available &&
    !hasTechnicalDependency(card.schema) &&
    validateIntakeValue(
      value,
      card.captureKind === "questionnaire"
        ? questionDraftSchema(card.schema)
        : card.schema,
    ).valid &&
    validBirthDate &&
    invalidFields.length === 0
  );
}
export function buildTopicRequest(
  selected: Row,
  role: string,
  topicId: string,
  changes: TopicChange[],
): TopicRequest {
  if (
    !selected?.id ||
    !Number.isSafeInteger(selected.revision) ||
    selected.revision < 1
  )
    throw new Error("Refresh the return before saving.");
  if (!changes.length) throw new Error("There are no changes to save.");
  const body: Row = { expectedRevision: selected.revision };
  const seen = new Set<string>();
  for (const change of changes) {
    const card = change.card,
      value = normalizeTaxAge(change.value);
    if (!canCapture(card.record, card.captureKind, role))
      throw new Error("Your current role cannot change this information.");
    if (!topicValueValid(card, value)) {
      const section =
        topics.find((topic) => topic.id === topicId)?.label || "Tax Interview";
      const validationIssues =
        card.record.id === "Q-008"
          ? filingStatusFieldIssues(value, "save-field", selected)
          : validateTaxFields(card.schema, value, {
              sectionId: topicId,
              sectionLabel: section,
              rootLabel: card.title,
              rootId: "save-field",
            });
      const issue =
        validationIssues.find((item) => item.kind === "invalid") ||
        validationIssues[0];
      throw new Error(
        issue
          ? `${issue.sectionLabel} — ${issue.fieldLabel}: ${issue.reason}`
          : `${section} — ${card.title}: This saved entry has a format this form cannot accept. Reload the original information or ask an administrator to review the entry.`,
      );
    }
    const container = topicContainer(card),
      id = card.record.id,
      key = topicKey(card);
    if (seen.has(key))
      throw new Error(
        "This section contains overlapping edits. Reload the return before saving.",
      );
    seen.add(key);
    body[container] ??= {};
    body[container][id] = cloneTopicValue(
      topicSaveValue(card as TaxTopicBinding, value, selected),
    );
  }
  // Local interview metadata is not a rule contract. Recompute managed checks from
  // the complete next draft before the existing revision-protected atomic Save.
  // Household edits also refresh the managed filing answer, never hidden one-time defaults.
  const refreshed = {
    ...selected,
    facts: { ...selected.facts, ...body.facts },
    answers: { ...selected.answers, ...body.answers },
  };
  const filingRaw = refreshed.answers["Q-008"];
  const refreshFiling =
    Object.hasOwn(body.answers ?? {}, "Q-008") ||
    Object.hasOwn(body.answers ?? {}, "Q-009") ||
    [
      "taxpayer.claimable_status",
      "taxpayer.dependent_count",
      "taxpayer.qualifying_person_count",
      "taxpayer.dependency_tests",
    ].some((id) => Object.hasOwn(body.facts ?? {}, id));
  if (
    refreshFiling &&
    isTaxObject(filingRaw) &&
    filingInterviewValid(filingRaw.interview)
  ) {
    const result = deriveFilingInterview(
      parseFilingInterview(filingRaw),
      refreshed,
      selected.answers?.["Q-008"],
    );
    body.answers ??= {};
    body.answers["Q-008"] = cloneTopicValue(result.answer);
    if (
      result.maritalFact !== undefined &&
      refreshed.facts["taxpayer.marital_status_year_end"] !== result.maritalFact
    ) {
      body.facts ??= {};
      body.facts["taxpayer.marital_status_year_end"] = result.maritalFact;
    }
  }
  return {
    returnId: selected.id,
    revision: selected.revision,
    topicId,
    url: `/cases/${encodeURIComponent(selected.id)}`,
    body,
  };
}
export async function saveTopicRequest(
  request: TopicRequest,
  submit: (url: string, body: unknown) => Promise<Row> = patch,
): Promise<Row> {
  const response = await submit(request.url, request.body);
  let confirmed =
    response?.id === request.returnId &&
    Number.isSafeInteger(response?.revision) &&
    response.revision === request.revision + 1;
  for (const container of ["facts", "answers"])
    for (const [id, value] of Object.entries(request.body[container] || {})) {
      confirmed =
        confirmed &&
        Object.prototype.hasOwnProperty.call(response?.[container] || {}, id) &&
        topicValueKey(response[container][id]) === topicValueKey(value);
    }
  if (!confirmed)
    throw new Error(
      "The save could not be confirmed. Refresh before trying again; your changes may already have been saved.",
    );
  return response;
}
